import { NavigationSection, OrderStatusDisplay } from '@/types/NavigationTypes'
import { isOrderEditable } from '@/utils/OrderEditability'
import { Check, Clock, Refresh } from 'flowbite-react-icons/outline'
import React from 'react'

interface NavigationProps {
  activeSection: NavigationSection
  onNavigationClick: (section: NavigationSection) => void
  hasUnsavedChanges?: boolean
  orderStatus?: OrderStatusDisplay
}

const Navigation: React.FC<NavigationProps> = ({
  activeSection,
  onNavigationClick,
  hasUnsavedChanges = false,
  orderStatus = OrderStatusDisplay.ORDER_PLACED,
}) => {
  // Helper to determine if a status step is complete
  const isStatusComplete = (status: OrderStatusDisplay): boolean => {
    const statusOrder = [
      OrderStatusDisplay.ORDER_PLACED,
      OrderStatusDisplay.ORDER_SUBMITTED,
      OrderStatusDisplay.SAMPLE_RECEIVED,
      OrderStatusDisplay.RESULT_READY,
    ]
    const currentIndex = statusOrder.indexOf(orderStatus)
    const checkIndex = statusOrder.indexOf(status)
    return checkIndex < currentIndex
  }

  // Helper to determine if a status step is current
  const isStatusCurrent = (status: OrderStatusDisplay): boolean => {
    return orderStatus === status
  }

  // Check if results are ready (for showing Lab Report nav)
  const isResultReady = orderStatus === OrderStatusDisplay.RESULT_READY

  // Check if HPO editing is allowed
  const isHPOEditable = isOrderEditable(orderStatus)

  // Get status-specific title and description
  const getStatusContent = () => {
    switch (orderStatus) {
      case OrderStatusDisplay.ORDER_PLACED:
        return {
          title: 'Order Placed',
          description:
            'Your requisition has been placed. You may continue updating HPO until the sample begins processing.',
        }
      case OrderStatusDisplay.ORDER_SUBMITTED:
        return {
          title: 'Order Submitted',
          description:
            'Your requisition has been submitted. You may continue updating HPO until the sample begins processing.',
        }
      case OrderStatusDisplay.SAMPLE_RECEIVED:
        return {
          title: 'Sample Received',
          description: 'HPO updates closed. The lab has started processing the sample.',
        }
      case OrderStatusDisplay.RESULT_READY:
        return {
          title: 'Test Results Available',
          description: 'Your genetic test results are ready for review.',
        }
      default:
        return {
          title: 'Order Submitted',
          description:
            'Your requisition has been submitted. You may continue updating HPO until the sample begins processing.',
        }
    }
  }

  const statusContent = getStatusContent()

  return (
    <div className="flex w-[298px] shrink-0 flex-col items-start gap-2.5 border-r border-border-base dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800 p-6">
      <div className="flex flex-col space-y-6">
        {/* Success Indicator */}
        <div className="flex flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success-100 dark:bg-gray-900">
            <Check className="h-8 w-8 text-text-fg-success-strong dark:text-success-100" />
          </div>
          <h1 className="mb-2 mt-4 text-3xl font-bold text-text-heading dark:text-white">{statusContent.title}</h1>
          <p className="mt-2 text-sm leading-5 font-medium text-text-heading dark:text-white">{statusContent.description}</p>
        </div>

        {/* Updates Ready to Send - Show when there are unsaved changes */}
        {hasUnsavedChanges && (
          <div className="w-full rounded-base border border-warning-300 dark:border-warning-600 bg-warning-50 dark:bg-gray-900 p-4">
            <div className="flex items-center gap-2">
              <Refresh className="h-5 w-5 text-text-fg-warning dark:text-warning-100" />
              <p className="text-xs leading-4 text-text-subtle dark:text-gray-500">You have unsaved changes that have not been sent to the lab.</p>
            </div>
          </div>
        )}

        {/* Navigation Links */}
        <div className="w-full">
          <nav className="space-y-2">
            <button
              onClick={() => onNavigationClick('order-summary')}
              className={`relative flex w-full items-center justify-between rounded-base px-3 py-2 transition-colors hover:bg-background-tertiary dark:hover:bg-gray-700 ${
                activeSection === 'order-summary' ? 'bg-success-100 dark:bg-gray-900' : ''
              }`}
            >
              <span className="self-stretch text-sm leading-5 font-medium tracking-tight text-text-heading dark:text-white">Order Summary</span>
              <Check className="h-5 w-5 text-text-fg-success-strong dark:text-success-100" />
            </button>
            <button
              onClick={() => onNavigationClick('hpo-terms')}
              className={`relative flex w-full items-center justify-between rounded-base px-3 py-2 transition-colors hover:bg-background-tertiary dark:hover:bg-gray-700 ${
                activeSection === 'hpo-terms' ? 'bg-success-100 dark:bg-gray-900' : ''
              }`}
            >
              <span className="self-stretch text-sm leading-5 font-medium tracking-tight text-text-heading dark:text-white">HPO Terms</span>
              {isHPOEditable ? (
                <span className="text-sm font-medium text-text-fg-success-strong dark:text-success-100 hover:underline">Edit</span>
              ) : (
                <Check className="h-5 w-5 text-text-fg-success-strong dark:text-success-100" />
              )}
            </button>
            <button
              onClick={() => onNavigationClick('requisition-form')}
              className={`relative flex w-full items-center justify-between rounded-base px-3 py-2 transition-colors hover:bg-background-tertiary dark:hover:bg-gray-700 ${
                activeSection === 'requisition-form' ? 'bg-success-100 dark:bg-gray-900' : ''
              }`}
            >
              <span className="self-stretch text-sm leading-5 font-medium tracking-tight text-text-heading dark:text-white">Requisition Form</span>
              <Check className="h-5 w-5 text-text-fg-success-strong dark:text-success-100" />
            </button>
            {/* Lab Report - Only show when results are ready */}
            {isResultReady && (
              <button
                onClick={() => onNavigationClick('lab-report')}
                className={`relative flex w-full items-center justify-between rounded-base px-3 py-2 transition-colors hover:bg-background-tertiary dark:hover:bg-gray-700 ${
                  activeSection === 'lab-report' ? 'bg-success-100 dark:bg-gray-900' : ''
                }`}
              >
                <span className="self-stretch text-sm leading-5 font-medium tracking-tight text-text-heading dark:text-white">Lab Report</span>
                <Check className="h-5 w-5 text-text-fg-success-strong dark:text-success-100" />
              </button>
            )}
          </nav>
        </div>

        {/* Order Status */}
        <div className="w-full">
          <h2 className="mb-4 self-stretch text-sm leading-5 font-medium tracking-tight text-text-heading dark:text-white">Order Status</h2>
          <div className="relative">
            {/* Connection lines between status items */}
            <div className="absolute bottom-6 left-3 top-6 w-0.5 bg-background-quaternary dark:bg-gray-800"></div>
            <div className="relative space-y-4">
              {/* Order Placed */}
              <div className="relative flex items-center">
                <div
                  className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ring-2 ring-white ${
                    isStatusComplete(OrderStatusDisplay.ORDER_PLACED) ||
                    isStatusCurrent(OrderStatusDisplay.ORDER_PLACED)
                      ? 'bg-success-100 dark:bg-gray-900 text-text-fg-success-strong dark:text-success-100'
                      : 'border-2 border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900'
                  }`}
                >
                  {(isStatusComplete(OrderStatusDisplay.ORDER_PLACED) ||
                    isStatusCurrent(OrderStatusDisplay.ORDER_PLACED)) && (
                    <Check className="h-4 w-4" />
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className="text-xs leading-4 font-medium text-text-subtle dark:text-gray-500"
                  >
                    Order Placed
                  </p>
                </div>
              </div>

              {/* Order Submitted */}
              <div className="relative flex items-center">
                <div
                  className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ring-2 ring-white ${
                    isStatusComplete(OrderStatusDisplay.ORDER_SUBMITTED)
                      ? 'bg-success-100 dark:bg-gray-900 text-text-fg-success-strong dark:text-success-100'
                      : isStatusCurrent(OrderStatusDisplay.ORDER_SUBMITTED)
                        ? 'bg-info-100 dark:bg-gray-900 text-info-600 dark:text-info-100'
                        : 'border-2 border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900'
                  }`}
                >
                  {isStatusComplete(OrderStatusDisplay.ORDER_SUBMITTED) && (
                    <Check className="h-4 w-4" />
                  )}
                  {isStatusCurrent(OrderStatusDisplay.ORDER_SUBMITTED) && (
                    <Clock className="h-4 w-4" />
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className="text-xs leading-4 font-medium text-text-subtle dark:text-gray-500"
                  >
                    Processing
                  </p>
                </div>
              </div>

              {/* Result Pending */}
              <div className="relative flex items-center">
                <div
                  className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ring-2 ring-white ${
                    isStatusComplete(OrderStatusDisplay.SAMPLE_RECEIVED)
                      ? 'bg-success-100 dark:bg-gray-900 text-text-fg-success-strong dark:text-success-100'
                      : isStatusCurrent(OrderStatusDisplay.SAMPLE_RECEIVED)
                        ? 'bg-info-100 dark:bg-gray-900 text-info-600 dark:text-info-100'
                        : 'border-2 border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900'
                  }`}
                >
                  {isStatusComplete(OrderStatusDisplay.SAMPLE_RECEIVED) && (
                    <Check className="h-4 w-4" />
                  )}
                  {isStatusCurrent(OrderStatusDisplay.SAMPLE_RECEIVED) && (
                    <Clock className="h-4 w-4" />
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className="text-xs leading-4 font-medium text-text-subtle dark:text-gray-500"
                  >
                    Result Pending
                  </p>
                </div>
              </div>

              {/* Result Ready */}
              <div className="relative flex items-center">
                <div
                  className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ring-2 ring-white ${
                    isStatusCurrent(OrderStatusDisplay.RESULT_READY)
                      ? 'bg-success-100 dark:bg-gray-900 text-text-fg-success-strong dark:text-success-100'
                      : 'border-2 border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900'
                  }`}
                >
                  {isStatusCurrent(OrderStatusDisplay.RESULT_READY) && (
                    <Check className="h-4 w-4" />
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className="text-xs leading-4 font-medium text-text-subtle dark:text-gray-500"
                  >
                    Result Ready
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Navigation
